import { UseHoverCardProps } from "@chakra-ui/react"
import { buttonProps } from "@pathwright/ux/src/components/chakra/themes/platform/theme"
import { LightMode } from "@pathwright/ux/src/components/chakra/ui/color-mode"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import { ReactNode } from "react"
import { PackageFragment } from "@pathwright/graphql-client/src/generated"
import PackageProvider, {
  PackageProviderProps,
  usePackageContext
} from "./PackageProvider"
import { PackageIdentity, PackagePopover, getPackageName } from "./PackageUI"

const Tag = ({
  positioning
}: {
  positioning?: UseHoverCardProps["positioning"]
}) => {
  const { theme, packageData } = usePackageContext()
  const { t } = useTranslate()

  if (!packageData) {
    return null
  }

  return (
    <PackagePopover
      renderTrigger={(Trigger) => (
        <LightMode>
          <Trigger
            aria-label={t("package_ui.view_details_for", {
              packageName: getPackageName(packageData),
              defaultValue: `View package details for ${getPackageName(
                packageData
              )}`
            })}
            type="button"
            cursor="pointer"
            borderRadius="full"
            py={1}
            px={3}
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
            border="none"
            transition="all 0.2s ease-in-out"
            {...buttonProps.variants.variant.secondaryGray}
            _hover={{
              bg: "rgba(153, 153, 153, 0.6)",
              // color: packageColor || "white",
              border: "none"
            }}
          >
            <PackageIdentity isTag={true} />
          </Trigger>
        </LightMode>
      )}
      positioning={positioning}
    />
  )
}

type RenderWithTag = (Tag: ReactNode, packageData: PackageFragment) => ReactNode

const PackageTag = ({
  renderWithTag,
  positioning
}: {
  renderWithTag?: RenderWithTag
  positioning?: UseHoverCardProps["positioning"]
}) => {
  const { packageData } = usePackageContext()

  if (!packageData) {
    return null
  }

  return renderWithTag ? (
    renderWithTag(<Tag positioning={positioning} />, packageData)
  ) : (
    <Tag positioning={positioning} />
  )
}

type PackageTagProps = {
  positioning?: UseHoverCardProps["positioning"]
  renderWithTag?: RenderWithTag
} & Omit<PackageProviderProps, "variant" | "actionButton" | "children">

const PackageTagContainer = ({
  renderWithTag,
  positioning,
  ...providerProps
}: PackageTagProps) => {
  return (
    <PackageProvider variant="tag" {...providerProps}>
      <PackageTag renderWithTag={renderWithTag} positioning={positioning} />
    </PackageProvider>
  )
}

export default PackageTagContainer
